(() => {
  if (customElements.get('mcase-color-customizer')) return;
  const views = ['back', 'front', 'left', 'right'];

  class MCaseCustomizer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) { this.observeLayout(); this.startLockscreenClock(); this.renderPresetPreviews(); return; }
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-mcase-config]').textContent);
      this.colors = new Map(this.config.palette.map(color => [color.id, color]));
      this.presets = (this.config.presets || []).filter(preset =>
        this.config.parts.every(part => this.colors.has(preset.colors?.[part.id])));
      this.previewMode = 'design';
      this.failedPhotos = new Set();
      this.presetImageUrls = [];
      this.selection = {};
      this.svgs = {};
      this.anchorCache = new Map();
      this.pickerId = `mcase-colors-${Math.random().toString(36).slice(2, 9)}`;
      this.hoverOpenedPicker = null;
      this.onOutsideClick = event => {
        if (this.activePicker && !this.querySelector(`[data-picker-container="${this.activePicker}"]`).contains(event.target)) {
          this.closeColorMenu(false);
        }
      };
      const params = new URL(location.href).searchParams;
      this.phoneColor = params.get('mcase-phone-color') === 'orange' ? 'orange' : 'black';
      this.chinStyle = params.get("mcase-chin-style") === "long" ? "long" : "short";
      this.chinReady = false;
      for (const part of this.config.parts) {
        const requested = params.get(`mcase-${part.id}`);
        this.selection[part.id] = this.colors.has(requested) ? requested : part.default;
      }
      this.textColors = new Map(this.config.text.colors.map(color => [color.id, color]));
      this.textFonts = new Map(this.config.text.fonts.map(font => [font.id, font]));
      this.customizationCopy = JSON.parse(this.querySelector('[data-mcase-customization-copy]').textContent);
      this.textSelection = this.readTextSelection(params);
      this.quantity = 1;
      this.basePrice = Number(this.dataset.basePrice ?? this.config.pricing.basePrice);
      this.currency = this.dataset.currency || this.config.pricing.currency;
      this.view = views.includes(params.get('mcase-view')) ? params.get('mcase-view') : 'back';
      // A new design must not restore the previous case, even if storage is unavailable.
      this.freshDesign = params.get('mcase-new') === '1';
      if (!this.freshDesign) this.restoreDraft(params);
      else {
        this.draftStorageKey = `mcase-customizer-draft-v1:${this.config.model}`;
        const url = new URL(location.href);
        url.searchParams.delete('mcase-new');
        history.replaceState(null, '', url);
      }
      this.renderControls();
      this.renderPresetControls();
      this.renderTextControls();
      this.querySelector('[data-text-enabled]').addEventListener('change', event => {
        this.textSelection.enabled = event.target.checked;
        this.closeColorMenu(false);
        this.view = 'back';
        this.clearTextUrl();
        this.render();
        this.status(this.querySelector('[data-text-intro]').textContent);
      });
      this.querySelectorAll('[data-customization-mode]').forEach(input => {
        input.addEventListener('change', () => {
          this.textSelection.mode = input.value;
          this.clearTextUrl();
          this.closeColorMenu(false);
          this.view = 'back';
          this.render();
          this.status(this.querySelector('[data-text-intro]').textContent);
        });
      });
      const quantityInput = this.querySelector('[data-mcase-quantity]');
      quantityInput.value = String(this.quantity);
      quantityInput.addEventListener('input', () => {
        this.quantity = quantityInput.validity.valid && quantityInput.value ? Number(quantityInput.value) : 1;
        this.renderPricing();
      });
      const commitQuantity = () => {
        this.quantity = quantityInput.validity.valid && quantityInput.value ? Number(quantityInput.value) : 1;
        quantityInput.value = String(this.quantity);
        this.renderPricing();
      };
      quantityInput.addEventListener('change', commitQuantity);
      quantityInput.addEventListener('blur', commitQuantity);
      this.querySelector('[data-backplate-text-input]').addEventListener('input', event => {
        this.textSelection.text = this.normalizeBackplateText(event.target.value);
        this.clearTextUrl();
        this.view = 'back';
        this.closeColorMenu(false);
        this.render();
      });
      this.querySelector('[data-backplate-text-input]').addEventListener('focus', () => {
        if (this.view !== 'back') {
          this.closeColorMenu(false);
          this.view = 'back';
          this.render();
        }
      });
      this.addEventListener('click', event => this.onClick(event));
      const moduleControls = this.querySelector('[data-module-controls]');
      moduleControls.addEventListener('pointerover', event => this.onPickerPointerOver(event));
      moduleControls.addEventListener('pointerout', event => this.onPickerPointerOut(event));
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.activePicker) {
          event.preventDefault();
          this.closeColorMenu(true);
        }
      });
      this.querySelector('[data-preview-dialog]').addEventListener('click', event => {
        if (event.target === event.currentTarget) event.currentTarget.close();
      });
      this.render();
      this.observeLayout();
      this.startLockscreenClock();
      this.loadViews();
    }

    observeLayout() {
      this.resizeObserver ||= new ResizeObserver(() => this.scheduleCalloutLayout());
      this.resizeObserver.observe(this.querySelector('[data-mcase-stage]'));
      document.addEventListener('click', this.onOutsideClick);
      document.fonts.ready.then(() => this.scheduleCalloutLayout());
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.layoutFrame);
      document.removeEventListener('click', this.onOutsideClick);
      clearInterval(this.lockscreenClock);
      clearTimeout(this.hoverCloseTimer);
      this.lockscreenClock = null;
      this.presetImageUrls.forEach(url => URL.revokeObjectURL(url));
      this.presetImageUrls = [];
    }

    status(message) {
      this.querySelector('[data-mcase-status]').textContent = message;
    }

    normalizeBackplateText(value) {
      return Array.from(value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim()).slice(0, this.config.text.maxLength).join('');
    }

    readTextSelection(params) {
      const text = this.normalizeBackplateText(params.get('mcase-text') ?? '');
      const color = this.textColors.has(params.get('mcase-text-color')) ? params.get('mcase-text-color') : this.config.text.defaultColor;
      // Legacy links with a changed word/color restore as an explicitly priced add-on.
      const legacyCustom = params.has('mcase-text') && Boolean(text) &&
        (text !== this.config.text.default || color !== this.config.text.defaultColor);
      const mode = params.get('mcase-customization') === 'blank' ? 'blank' : 'text';
      const enabled = params.has('mcase-custom-text') ? params.get('mcase-custom-text') === '1' : mode === 'blank' || legacyCustom;
      const font = this.textFonts.has(params.get('mcase-text-font')) ? params.get('mcase-text-font') : 'outfit';
      const size = ['s','m','l'].includes(params.get('mcase-text-size')) ? params.get('mcase-text-size') : 'm';
      const align = ['left','center','right'].includes(params.get('mcase-text-align')) ? params.get('mcase-text-align') : 'center';
      const outline = ['none','black','white'].includes(params.get('mcase-text-outline')) ? params.get('mcase-text-outline') : 'none';
      return { enabled, mode, text: enabled ? text : '', color: enabled ? color : this.config.text.defaultColor, font, size, align, outline };
    }

    clearTextUrl() {
      const url = new URL(location.href);
      for (const key of ['mcase-customization', 'mcase-custom-text', 'mcase-text', 'mcase-text-color', 'mcase-text-font', 'mcase-text-size', 'mcase-text-align', 'mcase-text-outline']) url.searchParams.delete(key);
      history.replaceState(null, '', url);
    }

    draftUrlKey(params = new URL(location.href).searchParams) {
      const keys = [...this.config.parts.map(part => `mcase-${part.id}`),
        'mcase-phone-color', 'mcase-view', 'mcase-chin-style', 'mcase-customization', 'mcase-custom-text', 'mcase-text',
        'mcase-text-color', 'mcase-text-font', 'mcase-text-size',
        'mcase-text-align', 'mcase-text-outline'];
      return new URLSearchParams(keys.filter(key => params.has(key))
        .map(key => [key, params.get(key)])).toString();
    }

    restoreDraft(params) {
      this.draftStorageKey = `mcase-customizer-draft-v1:${this.config.model}`;
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(this.draftStorageKey) || 'null'); }
      catch { return; }
      if (!saved || saved.version !== 1) return;
      const urlKey = this.draftUrlKey(params);
      const isReload = performance.getEntriesByType('navigation')[0]?.type === 'reload';
      // A fresh shared link wins. Reloads resume edits made after opening that link,
      // even when its original URL still contains the old color/text parameters.
      if (urlKey && !(isReload && saved.urlKey === urlKey)) return;
      for (const part of this.config.parts) {
        if (this.colors.has(saved.colors?.[part.id])) this.selection[part.id] = saved.colors[part.id];
      }
      this.previewMode = saved.previewMode === 'photo' ? 'photo' : 'design';
      this.phoneColor = saved.phoneColor === 'orange' ? 'orange' : 'black';
      this.chinStyle = saved.chinStyle === 'long' ? 'long' : 'short';
      this.view = views.includes(saved.view) ? saved.view : 'back';
      if (Number.isSafeInteger(saved.quantity) && saved.quantity >= 1) this.quantity = saved.quantity;
      const text = saved.textSelection;
      this.textSelection = {
        enabled: text?.enabled === true,
        mode: text?.mode === 'blank' ? 'blank' : 'text',
        text: this.normalizeBackplateText(typeof text?.text === 'string' ? text.text : ''),
        color: this.textColors.has(text?.color) ? text.color : this.config.text.defaultColor,
        font: this.textFonts.has(text?.font) ? text.font : 'outfit',
        size: ['s', 'm', 'l'].includes(text?.size) ? text.size : 'm',
        align: ['left', 'center', 'right'].includes(text?.align) ? text.align : 'center',
        outline: ['none', 'black', 'white'].includes(text?.outline) ? text.outline : 'none'
      };
    }

    saveDraft() {
      try {
        sessionStorage.setItem(this.draftStorageKey, JSON.stringify({
          version: 1, urlKey: this.draftUrlKey(), colors: this.selection,
          previewMode: this.previewMode, phoneColor: this.phoneColor, chinStyle: this.chinStyle, view: this.view, quantity: this.quantity,
          textSelection: this.textSelection
        }));
      } catch { /* Storage may be unavailable; the customizer still works. */ }
    }

    displayedBackplateText(selection = this.textSelection) {
      return selection.enabled ? selection : { text: this.config.text.default, color: this.config.text.defaultColor };
    }

    renderTextState() {
      const toggle = this.querySelector('[data-text-enabled]');
      const fields = this.querySelector('[data-text-fields]');
      const choices = this.querySelector('[data-customization-options]');
      const customText = this.textSelection.enabled && this.textSelection.mode !== 'blank';
      toggle.checked = this.textSelection.enabled;
      toggle.setAttribute('aria-expanded', String(this.textSelection.enabled));
      choices.hidden = choices.disabled = !this.textSelection.enabled;
      fields.hidden = fields.disabled = !customText;
      this.querySelectorAll('[data-customization-mode]').forEach(input => {
        input.checked = input.value === this.textSelection.mode;
      });
      this.querySelector('[data-text-intro]').textContent = this.textSelection.enabled
        ? this.customizationCopy[this.textSelection.mode === 'blank' ? 'blankIntro' : 'textIntro']
        : this.customizationCopy.defaultIntro;
      const input = this.querySelector('[data-backplate-text-input]');
      const valid = !customText || Boolean(this.textSelection.text);
      input.required = customText;
      input.setCustomValidity(valid ? '' : this.customizationCopy.textError);
      input.setAttribute('aria-invalid', String(!valid));
      this.querySelector('[data-text-error]').hidden = valid;
      this.dataset.customTextValid = String(valid);
    }

    priceBreakdown() {
      const cases = this.basePrice * this.quantity;
      const customization = this.textSelection.enabled ? this.config.pricing.textFee * this.quantity : 0;
      return { cases, customization, total: cases + customization };
    }

    renderPricing() {
      this.saveDraft();
      const summary = this.querySelector('[data-price-summary]');
      // The confirmed fee is USD. Other currencies need a Shopify-priced add-on.
      summary.hidden = !Number.isSafeInteger(this.basePrice);
      if (summary.hidden) return;
      const money = cents => this.commerce ? this.commerce.money(cents) : `US$${(cents / 100).toFixed(2)}`;
      const prices = this.priceBreakdown();
      this.querySelector('[data-case-subtotal]').textContent = money(prices.cases);
      this.querySelector('[data-text-fee-row]').hidden = !this.textSelection.enabled;
      const label = this.customizationCopy[this.textSelection.mode === 'blank' ? 'blankLabel' : 'textLabel'];
      this.querySelector('[data-text-fee-label]').textContent = `${label} × ${this.quantity}`;
      this.querySelector('[data-text-fee]').textContent = money(prices.customization);
      this.querySelector('[data-total-price]').textContent = money(prices.total);
      this.dispatchEvent(new CustomEvent('mcase:price-change'));
    }

    renderTextControls() {
      const input = this.querySelector('[data-backplate-text-input]');
      input.value = this.textSelection.text;
      input.maxLength = this.config.text.maxLength;
      const help = this.querySelector('[data-text-help]');
      help.id = `${this.pickerId}-text-help`;
      const error = this.querySelector('[data-text-error]');
      error.id = `${this.pickerId}-text-error`;
      input.setAttribute('aria-describedby', `${help.id} ${error.id}`);
      const fields = this.querySelector('[data-text-fields]');
      fields.id = `${this.pickerId}-text-fields`;
      const choices = this.querySelector('[data-customization-options]');
      choices.id = `${this.pickerId}-customization-options`;
      this.querySelector('[data-text-enabled]').setAttribute('aria-controls', choices.id);
      this.querySelectorAll('[data-customization-mode]').forEach(input => {
        input.name = `${this.pickerId}-customization-mode`;
      });

      const tools = document.createElement('div');
      tools.className = 'sm-mcase__text-style';
      const groups = [
        ['size', 'Text size', [['s', 'S'], ['m', 'M'], ['l', 'L']]],
        ['align', 'Align', [['left', 'Left'], ['center', 'Center'], ['right', 'Right']]],
        ['outline', 'Outline', [['none', 'None'], ['black', 'Black'], ['white', 'White']]]
      ];
      const content = (key, value, label) => {
        if (key === 'align') return `<span class="sm-mcase__align-icon sm-mcase__align-icon--${value}" aria-hidden="true"><span></span><span></span><span></span></span>`;
        if (key === 'outline') return `<span class="sm-mcase__outline-icon sm-mcase__outline-icon--${value}" aria-hidden="true">A</span>`;
        return label;
      };
      tools.innerHTML = `<div class="sm-mcase__text-entry">
        <div class="sm-mcase__text-input-field"></div>
        <label class="sm-mcase__font-label"><span>Font</span><select data-text-font aria-label="Font">${[...this.textFonts.values()].map(font => `<option value="${font.id}">${font.label}</option>`).join('')}</select></label>
        <div class="sm-mcase__text-messages"></div></div>
        <div class="sm-mcase__text-tools">${groups.map(([key, title, options]) => `<div class="sm-mcase__text-effect-group sm-mcase__text-effect-group--${key}"><span>${title}</span><div class="sm-mcase__segmented" role="group" aria-label="${title}">${options.map(([value, label]) => `<button type="button" data-text-style="${key}" data-style-value="${value}" aria-label="${title}: ${label}" title="${title}: ${label}" aria-pressed="false">${content(key, value, label)}</button>`).join('')}</div></div>`).join('')}</div>
        <div class="sm-mcase__text-color-field"></div>`;

      const textLabel = this.querySelector('.sm-mcase__text-label');
      textLabel.querySelector('span').classList.remove('visually-hidden');
      const textField = tools.querySelector('.sm-mcase__text-input-field');
      textField.append(textLabel);
      tools.querySelector('.sm-mcase__text-messages').append(help, error);
      const colorField = tools.querySelector('.sm-mcase__text-color-field');
      const controls = this.querySelector('[data-text-colors]');
      colorField.append(this.querySelector('.sm-mcase__text-color-label'), controls);
      fields.append(tools);

      this.querySelector('[data-text-font]').value = this.textSelection.font;
      this.querySelector('[data-text-font]').addEventListener('change', event => this.updateTextStyle('font', event.target.value));
      tools.addEventListener('click', event => {
        const button = event.target.closest('[data-text-style]');
        if (button) this.updateTextStyle(button.dataset.textStyle, button.dataset.styleValue);
      });
      for (const color of this.textColors.values()) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sm-mcase__text-color';
        button.dataset.textColor = color.id;
        button.style.setProperty('--text-swatch', color.hex);
        button.setAttribute('aria-label', `Use ${color.label} text color`);
        button.setAttribute('title', color.label);
        button.setAttribute('aria-pressed', String(color.id === this.textSelection.color));
        controls.append(button);
      }
    }

    updateTextStyle(key, value) {
      this.textSelection[key] = value;
      this.clearTextUrl();
      this.view = 'back';
      this.closeColorMenu(false);
      this.render();
      const font = this.textFonts.get(this.textSelection.font);
      document.fonts.load(`${font.weight} 100px ${font.family}`).then(() => this.render()).catch(() => {});
    }

    applyBackplateText(root = this, selection = this.textSelection) {
      const customTextEnabled = selection.enabled && selection.mode !== 'blank';
      for (const logo of root.querySelectorAll('[data-role="backplate-logo"]')) {
        logo.setAttribute('display', selection.enabled ? 'none' : 'inline');
      }
      selection = this.displayedBackplateText(selection);
      // Measure independently of SVG visibility so side/front navigation and
      // detached export clones retain the same fitted text size.
      this.textMeasureCanvas ||= document.createElement('canvas');
      const context = this.textMeasureCanvas.getContext('2d');
      const font = this.textFonts.get(selection.font) || this.textFonts.get('outfit');
      context.font = `${font.weight} 1000px ${font.family}, Arial, sans-serif`;
      const widthAtOne = context.measureText(selection.text).width / 1000;
      // Calibrate M against the actual smallsmore glyph height, not the font's em box.
      // The CAD/SVG coordinates use millimetres; keep the existing S/M/L proportions.
      const reference = context.measureText(this.config.text.default);
      const referenceHeightAtOne = (reference.actualBoundingBoxAscent + reference.actualBoundingBoxDescent) / 1000;
      const mediumSize = this.config.text.referenceHeightMm && referenceHeightAtOne > 0
        ? this.config.text.referenceHeightMm / referenceHeightAtOne : this.config.text.fontSize;
      const requestedSize = mediumSize * ({s:1/1.18,m:1,l:1.36/1.18}[selection.size] || 1);
      const fontSize = Math.min(requestedSize, widthAtOne ? (this.config.text.maxWidth - 1) / widthAtOne : requestedSize);
      for (const node of root.querySelectorAll('[data-role="backplate-text"]')) {
        node.setAttribute('display', customTextEnabled ? 'inline' : 'none');
        node.textContent = selection.text;
        node.setAttribute('fill', this.textColors.get(selection.color).hex);
        node.setAttribute('font-size', String(fontSize));
        node.setAttribute('letter-spacing', '0');
        node.setAttribute('font-family', `${font.family}, Arial, sans-serif`);
        node.setAttribute('font-weight', String(font.weight));
        node.setAttribute('x', String({left:22.265, center:48.765, right:75.265}[selection.align] || 48.765));
        node.setAttribute('text-anchor', {left:'start',center:'middle',right:'end'}[selection.align] || 'middle');
        node.setAttribute('stroke', selection.outline === 'black' ? '#111111' : selection.outline === 'white' ? '#ffffff' : 'none');
        // Paint the stroke first, then the unchanged fill, leaving only the outer outline visible.
        node.setAttribute('stroke-width', selection.outline && selection.outline !== 'none' ? String(fontSize * .08) : '0');
        node.setAttribute('paint-order', 'stroke fill');
        node.setAttribute('stroke-linejoin', 'round');
      }
    }

    startLockscreenClock() {
      this.timeFormatter ||= new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
      this.dateFormatter ||= new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
      clearInterval(this.lockscreenClock);
      this.updateLockscreenClock();
      // Use the viewer's device clock; repaint only when the displayed value changes.
      this.lockscreenClock = setInterval(() => this.updateLockscreenClock(), 1000);
    }

    applyModelLogos(root = this, selection = this.selection) {
      for (const logo of root.querySelectorAll('[data-role="case-logo"]')) {
        const color = this.colors.get(selection[logo.dataset.colorPart]);
        // Mix the selected material with 35% black for a subtle engraved mark.
        const darker = color.hex.replace(/[\da-f]{2}/gi, channel =>
          Math.round(parseInt(channel, 16) * .65).toString(16).padStart(2, '0'));
        logo.setAttribute('fill', darker);
      }
    }

    updateLockscreenClock(root = this, now = new Date()) {
      const time = this.timeFormatter.format(now);
      const date = this.dateFormatter.format(now);
      for (const [role, value] of [['time', time], ['date', date]]) {
        for (const node of root.querySelectorAll(`[data-role="lockscreen-${role}"]`)) {
          if (node.textContent !== value) node.textContent = value;
        }
      }
    }

    renderControls() {
      const controls = this.querySelector('[data-module-controls]');
      for (const part of this.config.parts) {
        const callout = document.createElement('div');
        callout.className = 'sm-mcase__callout';
        callout.dataset.pickerContainer = part.id;
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'sm-mcase__color-trigger';
        trigger.dataset.pickerOpen = part.id;
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-controls', `${this.pickerId}-${part.id}`);
        const name = document.createElement('span');
        name.className = 'sm-mcase__callout-name';
        name.textContent = part.label;
        const colorRow = document.createElement('span');
        colorRow.className = 'sm-mcase__callout-color';
        const dot = document.createElement('i');
        dot.dataset.selectedDot = part.id;
        dot.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span');
        label.dataset.selectedLabel = part.id;
        const arrow = document.createElement('span');
        arrow.className = 'sm-mcase__callout-arrow';
        arrow.textContent = '⌄';
        arrow.setAttribute('aria-hidden', 'true');
        colorRow.append(dot, label, arrow);
        const note = document.createElement('span');
        note.className = 'sm-mcase__callout-unseen';
        note.textContent = 'View on back';
        note.hidden = true;
        trigger.append(name, colorRow, note);
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'sm-mcase__color-menu';
        fieldset.id = `${this.pickerId}-${part.id}`;
        fieldset.dataset.module = part.id;
        fieldset.hidden = true;
        const legend = document.createElement('legend');
        legend.textContent = `${part.label} colors`;
        fieldset.append(legend);
        const swatches = document.createElement('div');
        swatches.className = 'sm-mcase__swatches';
        for (const color of this.config.palette) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'sm-mcase__color-option';
          button.dataset.color = color.id;
          button.dataset.module = part.id;
          button.style.setProperty('--swatch-color', color.hex);
          button.title = color.label;
          button.setAttribute('aria-label', `${part.label}: ${color.label}`);
          button.setAttribute('aria-pressed', 'false');
          const swatch = document.createElement('i');
          swatch.className = 'sm-mcase__option-dot';
          swatch.setAttribute('aria-hidden', 'true');
          const colorName = document.createElement('span');
          colorName.textContent = color.label;
          const check = document.createElement('span');
          check.className = 'sm-mcase__option-check';
          check.textContent = '✓';
          check.setAttribute('aria-hidden', 'true');
          button.append(swatch, colorName, check);
          swatches.append(button);
        }
        fieldset.append(swatches);
        callout.append(trigger, fieldset);
        controls.append(callout);
      }
    }

    renderPresetControls() {
      const controls = this.querySelector('[data-preset-controls]');
      for (const preset of this.presets) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sm-mcase__preset';
        button.dataset.preset = preset.id;
        button.setAttribute('aria-label', `Apply ${preset.label} colorway`);
        button.setAttribute('aria-pressed', 'false');
        button.title = this.config.parts.map(part => `${part.label}: ${this.colors.get(preset.colors[part.id]).label}`).join(', ');
        const image = document.createElement('img');
        image.className = 'sm-mcase__preset-preview';
        image.dataset.presetPreview = preset.id;
        image.alt = '';
        image.width = 40;
        image.height = 58;
        image.hidden = true;
        const details = document.createElement('span');
        details.className = 'sm-mcase__preset-details';
        const name = document.createElement('span');
        name.className = 'sm-mcase__preset-name';
        name.textContent = preset.label;
        const swatches = document.createElement('span');
        swatches.className = 'sm-mcase__preset-swatches';
        swatches.setAttribute('aria-hidden', 'true');
        for (const partId of ['frame', 'backplate', 'chin']) {
          const dot = document.createElement('i');
          dot.dataset.presetModule = partId;
          dot.style.background = this.colors.get(preset.colors[partId]).hex;
          swatches.append(dot);
        }
        const check = document.createElement('span');
        check.className = 'sm-mcase__preset-check';
        check.textContent = '✓';
        check.setAttribute('aria-hidden', 'true');
        details.append(name, swatches);
        button.append(image, details, check);
        controls.append(button);
      }
    }

    renderPresetPreviews() {
      if (!this.isConnected || !this.svgs.back || this.presetImageUrls.length) return;
      for (const preset of this.presets) {
        const svg = this.exportSvg('back', preset.colors, { enabled: false });
        svg.setAttribute('width', '80');
        svg.setAttribute('height', '116');
        const blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        this.presetImageUrls.push(url);
        const image = this.querySelector(`[data-preset-preview="${preset.id}"]`);
        image.src = url;
        image.hidden = false;
      }
    }

    closeColorMenu(returnFocus = false) {
      clearTimeout(this.hoverCloseTimer);
      if (!this.activePicker) return;
      const trigger = this.querySelector(`[data-picker-open="${this.activePicker}"]`);
      this.querySelector(`[data-module="${this.activePicker}"].sm-mcase__color-menu`).hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      this.querySelector(`[data-picker-container="${this.activePicker}"]`).classList.remove('is-open');
      this.activePicker = null;
      this.hoverOpenedPicker = null;
      if (returnFocus) trigger.focus({ preventScroll: true });
    }

    openColorMenu(id, focusOption = true) {
      const same = this.activePicker === id;
      this.closeColorMenu(false);
      if (same) return;
      const part = this.config.parts.find(part => part.id === id);
      if (!this.findCalloutAnchor(part) && this.svgs.back) {
        this.view = 'back';
        this.render();
      }
      this.activePicker = id;
      this.hoverOpenedPicker = focusOption ? null : id;
      const menu = this.querySelector(`[data-module="${id}"].sm-mcase__color-menu`);
      menu.hidden = false;
      this.querySelector(`[data-picker-container="${id}"]`).classList.add('is-open');
      this.querySelector(`[data-picker-open="${id}"]`).setAttribute('aria-expanded', 'true');
      this.scheduleCalloutLayout();
      if (focusOption) menu.querySelector('[aria-pressed="true"]').focus({ preventScroll: true });
    }

    onPickerPointerOver(event) {
      if (event.pointerType !== 'mouse' || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const card = event.target.closest('[data-picker-container]');
      if (!card || card.hidden) return;
      clearTimeout(this.hoverCloseTimer);
      const trigger = event.target.closest('[data-picker-open]');
      if (trigger && !trigger.disabled && this.activePicker !== trigger.dataset.pickerOpen) {
        this.openColorMenu(trigger.dataset.pickerOpen, false);
      }
    }

    onPickerPointerOut(event) {
      if (event.pointerType !== 'mouse') return;
      const card = event.target.closest('[data-picker-container]');
      if (!card || card.contains(event.relatedTarget) || this.hoverOpenedPicker !== card.dataset.pickerContainer) return;
      clearTimeout(this.hoverCloseTimer);
      this.hoverCloseTimer = setTimeout(() => {
        if (this.hoverOpenedPicker === card.dataset.pickerContainer && !card.matches(':hover') && !card.querySelector('.sm-mcase__color-menu').contains(document.activeElement)) {
          this.closeColorMenu(false);
        }
      }, 180);
    }

    findCalloutAnchor(part, compact = false) {
      const svg = this.svgs[this.view];
      if (!svg) return null;
      const key = `${this.view}:${part.id}:${compact && part.id === 'chin' ? 'bottom' : 'side'}`;
      if (this.anchorCache.has(key)) return this.anchorCache.get(key);
      const sideView = this.view === 'left' || this.view === 'right';
      const frameHeight = this.view === 'right' ? .87 : (sideView ? .7 : .28);
      const hints = { frame: [.025, frameHeight], camera: [1, .5], backplate: [.035, .75], chin: compact ? [.5, .9] : [.92, .75] };
      let best = null;
      for (const variable of part.variables) {
        const path = svg.querySelector(`[data-part="${variable}"] > path[d]:not([data-role="lighting"])`);
        if (!path?.getAttribute('d').trim()) continue;
        const box = path.getBBox();
        if (!box.width || !box.height) continue;
        const hint = hints[part.id] || [.5, .5];
        const target = part.id === 'buttons'
          ? { x: svg.viewBox.baseVal.width * .9, y: svg.viewBox.baseVal.height * .48 }
          : { x: box.x + box.width * hint[0], y: box.y + box.height * hint[1] };
        const candidate = svg.createSVGPoint();
        candidate.x = part.id === 'buttons' ? box.x + box.width / 2 : target.x;
        candidate.y = part.id === 'buttons' ? box.y + box.height / 2 : target.y;
        if (part.id === 'camera') {
          // Find the actual right edge at mid-height, including projected side views.
          let outside = box.x + box.width + box.width / 256;
          for (let step = 0; step <= 256; step++) {
            candidate.x = box.x + box.width * (1 - step / 256);
            if (path.isPointInFill(candidate)) {
              let inside = candidate.x;
              for (let refine = 0; refine < 16; refine++) {
                candidate.x = (inside + outside) / 2;
                if (path.isPointInFill(candidate)) inside = candidate.x;
                else outside = candidate.x;
              }
              best = { x: inside, y: target.y, distance: 0 };
              break;
            }
            outside = candidate.x;
          }
          if (best) break;
        }
        const points = [];
        if (path.isPointInFill(candidate)) points.push({ x: candidate.x, y: candidate.y });
        else {
          for (let x = 0; x < 7; x++) for (let y = 0; y < 7; y++) {
            candidate.x = box.x + box.width * (x + .5) / 7;
            candidate.y = box.y + box.height * (y + .5) / 7;
            if (path.isPointInFill(candidate)) points.push({ x: candidate.x, y: candidate.y });
          }
          if (!points.length) {
            const length = path.getTotalLength();
            for (let step = 0; step < 24; step++) points.push(path.getPointAtLength(length * step / 24));
          }
        }
        for (const point of points) {
          const distance = (point.x - target.x) ** 2 + (point.y - target.y) ** 2;
          if (!best || distance < best.distance) best = { x: point.x, y: point.y, distance };
        }
      }
      this.anchorCache.set(key, best);
      return best;
    }

    scheduleCalloutLayout() {
      cancelAnimationFrame(this.layoutFrame);
      if (this.isConnected) this.layoutFrame = requestAnimationFrame(() => this.layoutCallouts());
    }

    layoutCallouts() {
      const stage = this.querySelector('[data-mcase-stage]');
      let rect = stage.getBoundingClientRect();
      if (!rect.width) return;
      const compact = rect.width < 480;
      const phoneControl = this.querySelector('[data-phone-color-control]');
      const phoneControlBottom = phoneControl ? phoneControl.offsetTop + phoneControl.offsetHeight + 12 : 80;
      const chinControl = this.querySelector("[data-chin-fit-control]");
      const switchBand = chinControl.offsetHeight + parseFloat(getComputedStyle(chinControl).bottom);
      stage.dataset.medium = String(!compact && rect.width < 560);
      if (stage.dataset.compact !== String(compact)) {
        stage.dataset.compact = String(compact);
        this.scheduleCalloutLayout();
        return;
      }
      if (this.previewMode === 'photo') { this.alignPhoto(); return; }
      // Keep the sticky preview available throughout the text toggle's scroll range.
      // Otherwise the shorter collapsed panel pushes it against the grid's bottom,
      // and opening the controls makes it snap back to its sticky top position.
      const pageLayout = this.querySelector('.smallsmore-patch-customizer__layout');
      const previewWrap = this.querySelector('.sm-mcase__preview-wrap');
      const textOption = this.querySelector('.sm-mcase__text-option');
      if (pageLayout && previewWrap && textOption) {
        const textOffset = textOption.getBoundingClientRect().bottom - pageLayout.getBoundingClientRect().top;
        pageLayout.style.setProperty('--mcase-preview-context-height', `${Math.ceil(textOffset + previewWrap.offsetHeight)}px`);
      }
      // The context height can move a sticky preview; measure after that write.
      rect = stage.getBoundingClientRect();
      const lines = this.querySelector('[data-leader-lines]');
      lines.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`);
      lines.replaceChildren();
      const svg = this.svgs[this.view];
      const caseRect = svg?.querySelector('[data-part="frame"] > path[d]')?.getBoundingClientRect();
      const svgRect = svg?.getBoundingClientRect();
      const viewBox = svg?.viewBox.baseVal;
      // All four model SVGs use centered meet scaling. Read the visible bounds
      // instead of getScreenCTM(), which can lag the sticky position in Safari.
      const svgScale = svgRect && viewBox ? Math.min(svgRect.width / viewBox.width, svgRect.height / viewBox.height) : 0;
      const sideView = this.view === 'left' || this.view === 'right';
      const layout = { frame: ['left', .26], camera: ['right', .17], backplate: ['left', .75], buttons: ['right', .48], chin: ['right', .88] };
      if (sideView) {
        layout.camera = ['left', .26];
        layout.frame = ['left', .69];
      }
      if (this.view === 'right') {
        layout.camera = ['right', .26];
        layout.buttons = ['left', .48];
      }
      for (const part of this.config.parts) {
        const card = this.querySelector(`[data-picker-container="${part.id}"]`);
        if (card.hidden) continue;
        const anchor = this.findCalloutAnchor(part, compact);
        const [side, ratio] = layout[part.id];
        card.dataset.side = side;
        card.querySelector('.sm-mcase__callout-unseen').hidden = Boolean(anchor) || !svg;
        card.classList.toggle('is-unseen', !anchor);
        const cardWidth = compact ? (sideView && rect.width < 340 ? 100 : 116) : 108;
        card.style.width = `${cardWidth}px`;
        const top = part.id === 'camera' || (part.id === 'frame' && !sideView);
        let x = side === 'left' ? 12 : rect.width - cardWidth - 12;
        let y = rect.height * ratio - card.offsetHeight / 2;
        if (compact) {
          x = part.id === 'chin' ? (rect.width - cardWidth) / 2 : side === 'left' ? 16 : rect.width - cardWidth - 16;
          y = top ? Math.max(80, phoneControlBottom) : part.id === 'chin' ? rect.height - card.offsetHeight - 20 - switchBand : Math.max(rect.height - 160 - switchBand, (this.svgs[this.view]?.querySelector('[data-part="chin"] > path')?.getBoundingClientRect().bottom ?? rect.bottom - 180 - switchBand) - rect.top + 16);
          if (this.view === 'right' && part.id === 'buttons') {
            y = rect.height * ratio - card.offsetHeight / 2;
          }
        }
        if (!compact && side === 'left') y = Math.max(y, phoneControlBottom);
        card.style.left = `${x}px`;
        card.style.top = `${y}px`;
        if (anchor && svg) {
          const screen = {
            x: svgRect.left + (svgRect.width - viewBox.width * svgScale) / 2 + (anchor.x - viewBox.x) * svgScale,
            y: svgRect.top + (svgRect.height - viewBox.height * svgScale) / 2 + (anchor.y - viewBox.y) * svgScale
          };
          const ax = screen.x - rect.left, ay = screen.y - rect.top;
          let coords;
          if (compact) {
            const endX = x + cardWidth / 2, endY = top ? y + card.offsetHeight : y;
            if (this.view === 'right' && part.id === 'buttons') {
              const endX = x + cardWidth, endY = y + card.offsetHeight / 2;
              const channel = Math.max(endX + 4, (caseRect?.left ?? screen.x) - rect.left - 12);
              coords = [[ax, ay], [channel, ay], [channel, endY], [endX, endY]];
            } else if (part.id === 'chin') {
              const bendY = Math.min(endY - 12, Math.max(ay + 12, (caseRect?.bottom ?? screen.y) - rect.top + 12));
              coords = [[ax, ay], [ax, bendY], [endX, bendY], [endX, endY]];
            } else {
              const caseEdge = caseRect ? (side === 'left' ? caseRect.left : caseRect.right) - rect.left : ax;
              const channel = side === 'left' ? Math.max(12, Math.min(ax, caseEdge) - 12) : Math.min(rect.width - 12, Math.max(ax, caseEdge) + 12);
              const bendY = endY + (top ? 12 : -12);
              coords = [[ax, ay], [channel, ay], [channel, bendY], [endX, bendY], [endX, endY]];
            }
          } else {
            const endX = side === 'left' ? x + cardWidth : x, endY = y + card.offsetHeight / 2;
            const bendX = side === 'left' ? endX + 18 : endX - 18;
            coords = [[ax, ay], [bendX, endY], [endX, endY]];
          }
          const line = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
          line.dataset.leaderPart = part.id;
          line.setAttribute('points', coords.map(p => p.join(',')).join(' '));
          const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          dot.setAttribute('cx', ax); dot.setAttribute('cy', ay); dot.setAttribute('r', '2.5');
          lines.append(line, dot);
        }
        const menu = card.querySelector('.sm-mcase__color-menu');
        if (!menu.hidden) {
          const menuWidth = 192;
          const menuLeft = Math.max(8, Math.min(x, rect.width - menuWidth - 8));
          const below = y + card.offsetHeight + 8;
          const menuTop = below + menu.offsetHeight <= rect.height - 8 ? below : Math.max(8, y - menu.offsetHeight - 8);
          menu.style.left = `${menuLeft - x}px`;
          menu.style.top = `${menuTop - y}px`;
        }
      }
      stage.dataset.calloutsReady = 'true';
    }

    async loadViews() {
      const results = await Promise.allSettled(views.map(async view => {
        const response = await fetch(this.dataset[`${view}Url`]);
        if (!response.ok) throw new Error('Preview unavailable');
        const document = new DOMParser().parseFromString(await response.text(), 'image/svg+xml');
        if (document.querySelector('parsererror') || document.documentElement.localName !== 'svg') {
          throw new Error('Invalid preview');
        }
        const svg = document.documentElement;
        const prefix = `mcase-${view}-${Math.random().toString(36).slice(2, 8)}-`;
        const ids = new Map([...svg.querySelectorAll('[id]')].map(node => [node.id, prefix + node.id]));
        for (const node of svg.querySelectorAll('*')) {
          if (node.id) node.id = ids.get(node.id);
          for (const attr of [...node.attributes]) {
            let value = attr.value.replace(/url\(#([^)]+)\)/g, (match, id) => `url(#${ids.get(id) || id})`);
            if ((attr.name === 'href' || attr.name === 'xlink:href') && value.startsWith('#')) {
              value = '#' + (ids.get(value.slice(1)) || value.slice(1));
            }
            node.setAttribute(attr.name, value);
          }
        }
        svg.setAttribute('aria-label', `MCase for Titan 2 Elite, ${view} view`);
        svg.removeAttribute('aria-labelledby');
        this.svgs[view] = svg;
        this.querySelector(`[data-svg-view="${view}"]`).replaceChildren(svg);
      }));
      this.querySelector('[data-mcase-stage]').setAttribute('aria-busy', 'false');
      views.forEach((view, index) => {
        if (results[index].status === 'rejected') {
          const note = document.createElement('p');
          note.className = 'sm-mcase__caption';
          note.textContent = 'This view could not load. Refresh the page to try again.';
          this.querySelector(`[data-svg-view="${view}"]`).replaceChildren(note);
        }
      });
      const chinReady = await this.loadChinVariants();
      this.updateLockscreenClock();
      this.render();
      await Promise.allSettled([...this.textFonts.values()].map(font => document.fonts.load(`${font.weight} 100px ${font.family}`)));
      this.render();
      this.renderPresetPreviews();
      this.status(results.every(result => result.status === 'fulfilled') ? (chinReady ? 'Sample palette · Explore your colorway.' : 'Extended Chin could not load. Refresh to try again.') : 'A preview could not load. You can still choose colors.');
    }

    async loadChinVariants() {
      try {
        const response = await fetch(this.dataset.chinUrl);
        if (!response.ok) throw new Error('Extended chin unavailable');
        const data = await response.json();
        this.chinSnapshots = {};
        for (const view of views) {
          const svg = this.svgs[view];
          const model = data.views[view];
          if (!svg || !model?.path || !model.nativeEdges || model.viewBox !== svg.getAttribute('viewBox')) throw new Error('Chin geometry mismatch');
          const group = svg.querySelector('[data-part="chin"]');
          this.chinSnapshots[view] = {
            children: [...group.childNodes].map(node => node.cloneNode(true)),
            edges: [...svg.querySelectorAll('[data-source-part="chin"]')].map(node => ({ node, display: node.getAttribute('display') })),
            clips: [...svg.querySelectorAll('clipPath')].filter(node => node.id.endsWith('clip-chin')).map(node => ({ node: node.querySelector('path'), path: node.querySelector('path').getAttribute('d') }))
          };
        }
        this.chinModels = data.views;
        this.chinReady = true;
        return true;
      } catch (error) {
        this.chinStyle = 'short';
        this.chinReady = false;
        console.warn('Extended chin preview could not load:', error);
        return false;
      }
    }

    applyChinStyle() {
      if (!this.chinReady) return;
      for (const view of views) {
        const svg = this.svgs[view];
        if (svg.dataset.chinStyle === this.chinStyle) continue;
        const group = svg.querySelector('[data-part="chin"]');
        const saved = this.chinSnapshots[view];
        const model = this.chinModels[view];
        group.replaceChildren(...saved.children.map(node => node.cloneNode(true)));
        for (const edge of saved.edges) {
          if (this.chinStyle === 'long') edge.node.setAttribute('display', 'none');
          else if (edge.display === null) edge.node.removeAttribute('display');
          else edge.node.setAttribute('display', edge.display);
        }
        for (const clip of saved.clips) clip.node.setAttribute('d', this.chinStyle === 'long' ? model.path : clip.path);
        if (this.chinStyle === 'long') {
          group.querySelector('path').setAttribute('d', model.path);
          const ns = 'http://www.w3.org/2000/svg';
          const clipId = `${group.id}-extended-clip`;
          const defs = document.createElementNS(ns, 'defs');
          const clip = document.createElementNS(ns, 'clipPath');
          clip.id = clipId;
          const outline = document.createElementNS(ns, 'path');
          outline.setAttribute('d', model.path);
          outline.setAttribute('clip-rule', 'evenodd');
          clip.append(outline); defs.append(clip); group.append(defs);
          const edges = document.createElementNS(ns, 'path');
          for (const [name, value] of Object.entries({d:model.nativeEdges, 'data-role':'native-edges', fill:'none', stroke:'#808080', 'stroke-width':'.065', 'stroke-opacity':'.26', 'clip-path':`url(#${clipId})`, 'pointer-events':'none'})) edges.setAttribute(name, value);
          group.append(edges);
        }
        svg.dataset.chinStyle = this.chinStyle;
        svg.setAttribute('aria-label', `MCase for Titan 2 Elite, ${view} view, ${this.chinStyle === 'long' ? 'Extended Chin, approximately 3 mm longer' : 'Chin'}`);
        this.anchorCache.clear();
      }
    }

    matchingPhoto() {
      if (this.chinStyle !== 'short' || this.phoneColor !== 'black' || this.textSelection.enabled) return null;
      const preset = this.presets.find(item => this.config.parts.every(part => item.colors[part.id] === this.selection[part.id]));
      const photo = preset?.photos?.[this.view];
      const key = `${preset?.id}:${this.view}`;
      return photo?.src && !this.failedPhotos.has(key) ? { preset, photo, key } : null;
    }

    renderPhotoState() {
      const match = this.matchingPhoto();
      const toggle = this.querySelector('[data-photo-toggle]');
      if (!match) this.previewMode = 'design';
      const showingPhoto = this.previewMode === 'photo';
      toggle.hidden = !match;
      toggle.dataset.mode = this.previewMode;
      for (const button of toggle.querySelectorAll('[data-preview-mode]')) {
        button.setAttribute('aria-pressed', String(button.dataset.previewMode === this.previewMode));
      }
      const title = this.querySelector('.sm-mcase__assembly-title');
      title.firstElementChild.textContent = showingPhoto ? 'Real photos' : 'Build your own';
      title.querySelector('strong').hidden = showingPhoto;
      title.setAttribute('aria-label', showingPhoto ? 'Real photos' : 'Build your own MCase');
      this.querySelector('[data-phone-color-control]').hidden = showingPhoto;
      this.querySelector('[data-chin-fit-control]').hidden = showingPhoto;
      this.dataset.previewKind = showingPhoto ? 'photo' : 'design';
      const panel = this.querySelector('[data-photo-panel]');
      panel.hidden = !showingPhoto;
      this.querySelectorAll('[data-svg-view]').forEach(node => { node.hidden = node.dataset.svgView !== this.view; });
      this.querySelector('[data-preview-open]').disabled = showingPhoto ? false : !this.svgs[this.view];
      this.querySelector('.sm-mcase__callout-hint').textContent = showingPhoto
        ? `Real product photo · ${match.preset.label}`
        : 'Hover over a color label to choose a color. Tap on touchscreens.';
      if (!showingPhoto) return;
      const { photo, key, preset } = match;
      const window = this.querySelector('[data-photo-window]');
      window.style.setProperty('--photo-window-width', photo.width);
      this.alignPhoto();
      const image = this.querySelector('[data-photo-image]');
      image.style.width = `${photo.size / photo.width * 100}%`;
      image.style.left = `${photo.x / photo.width * 100}%`;
      image.style.top = `${photo.y / 800 * 100}%`;
      image.alt = `${preset.label} MCase, ${this.view === 'left' ? 'side with volume and shortcut buttons' : this.view === 'right' ? 'power-button side' : this.view + ' view'}, real product photo`;
      if (image.dataset.photoKey === key) return;
      image.hidden = true;
      this.querySelector('[data-photo-loading]').hidden = false;
      image.dataset.photoKey = key;
      image.onload = () => {
        if (image.dataset.photoKey !== key) return;
        image.hidden = false;
        this.querySelector('[data-photo-loading]').hidden = true;
        this.alignPhoto();
      };
      image.onerror = () => {
        if (image.dataset.photoKey !== key) return;
        this.failedPhotos.add(key);
        this.previewMode = 'design';
        this.render();
        this.saveDraft();
      };
      image.src = photo.src;
    }

    alignPhoto() {
      if (this.previewMode !== 'photo') return;
      const match = this.matchingPhoto();
      const svg = this.svgs[this.view];
      if (!match?.photo.subject || !svg) return;
      const rects = this.config.parts.flatMap(part => part.variables)
        .map(variable => svg.querySelector(`[data-part="${variable}"]`))
        .filter(node => node && getComputedStyle(node).display !== 'none')
        .map(node => node.getBoundingClientRect()).filter(rect => rect.width && rect.height);
      if (!rects.length) return;
      const left = Math.min(...rects.map(rect => rect.left));
      const top = Math.min(...rects.map(rect => rect.top));
      const width = Math.max(...rects.map(rect => rect.right)) - left;
      const height = Math.max(...rects.map(rect => rect.bottom)) - top;
      const stage = this.querySelector('[data-mcase-stage]').getBoundingClientRect();
      const { photo } = match;
      const subject = photo.subject;
      const scale = Math.min(width / subject.width, height / subject.height);
      const window = this.querySelector('[data-photo-window]');
      window.style.width = `${photo.width * scale}px`;
      window.style.height = `${800 * scale}px`;
      window.style.left = `${left - stage.left + (width - subject.width * scale) / 2 - subject.x * scale}px`;
      window.style.top = `${top - stage.top + (height - subject.height * scale) / 2 - subject.y * scale}px`;
    }

    renderChinControl() {
      const color = this.colors.get(this.selection.chin).hex;
      this.style.setProperty('--sm-chin-switch-color', color);
      const linear = color.match(/[\da-f]{2}/gi).map(channel => {
        const value = parseInt(channel, 16) / 255;
        return value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4;
      });
      const luminance = linear[0] * .2126 + linear[1] * .7152 + linear[2] * .0722;
      this.style.setProperty('--sm-chin-switch-ink', luminance > .179 ? 'var(--sm-color-text-primary)' : 'var(--sm-color-action-on-primary)');
      for (const button of this.querySelectorAll('[data-chin]')) {
        button.disabled = !this.chinReady;
        button.setAttribute('aria-pressed', String(button.dataset.chin === this.chinStyle));
      }
      this.querySelector('[data-chin-fit-note]').hidden = this.chinStyle !== 'long';
    }


    renderPhoneColor(root = this) {
      const orange = this.phoneColor === 'orange';
      for (const node of root.querySelectorAll('[data-phone-variant]')) {
        node.setAttribute('display', node.dataset.phoneVariant === this.phoneColor ? 'inline' : 'none');
      }
      for (const node of root.querySelectorAll('[data-part="phone-reference"][fill]')) {
        node.setAttribute('fill', orange ? '#f57839' : '#202124');
      }
      for (const button of root.querySelectorAll('[data-phone-color]')) {
        button.setAttribute('aria-pressed', String(button.dataset.phoneColor === this.phoneColor));
      }
    }

    render() {
      this.renderPhoneColor();
      this.applyChinStyle();
      for (const part of this.config.parts) {
        this.querySelector(`[data-picker-container="${part.id}"]`).hidden =
          (part.id === 'backplate' && this.view !== 'back') ||
          (part.id === 'camera' && this.view === 'front');
        const color = this.colors.get(this.selection[part.id]);
        this.querySelector(`[data-selected-label="${part.id}"]`).textContent = color.label;
        this.querySelector(`[data-selected-dot="${part.id}"]`).style.background = color.hex;
        const trigger = this.querySelector(`[data-picker-open="${part.id}"]`);
        const label = part.id === 'chin' && this.chinStyle === 'long' ? 'Extended Chin' : part.label;
        trigger.setAttribute('aria-label', `Choose ${label} color, ${color.label}`);
        this.querySelector(`[data-picker-container="${part.id}"] .sm-mcase__callout-name`).textContent = label;
        this.querySelector(`[data-picker-container="${part.id}"] legend`).textContent = `${label} colors`;
        trigger.disabled = !this.svgs[this.view] && !this.svgs.back;
        for (const button of this.querySelectorAll(`button[data-module="${part.id}"]`)) {
          button.setAttribute('aria-pressed', String(button.dataset.color === color.id));
        }
        for (const svg of Object.values(this.svgs)) {
          for (const variable of part.variables) svg.style.setProperty(`--${variable}-color`, color.hex);
        }
      }
      for (const letter of this.querySelectorAll('[data-title-part]')) {
        const color = this.colors.get(this.selection[letter.dataset.titlePart]);
        if (color) letter.style.color = color.hex;
      }
      const matchedPreset = this.presets.find(preset => this.config.parts.every(part => preset.colors[part.id] === this.selection[part.id]));
      for (const button of this.querySelectorAll('[data-preset]')) {
        button.setAttribute('aria-pressed', String(button.dataset.preset === matchedPreset?.id));
      }
      this.querySelector('[data-current-preset]').textContent = matchedPreset?.label || 'Custom colorway';
      this.querySelectorAll('[data-svg-view]').forEach(node => { node.hidden = node.dataset.svgView !== this.view; });
      this.querySelectorAll('[data-view]').forEach(button => { button.setAttribute('aria-pressed', String(button.dataset.view === this.view)); });
      this.querySelector('[data-preview-open]').disabled = !this.svgs[this.view];
      this.renderChinControl();
      this.applyBackplateText();
      this.applyModelLogos();
      this.renderTextState();
      this.querySelector('[data-text-font]').value = this.textSelection.font;
      for (const button of this.querySelectorAll('[data-text-style]')) {
        button.setAttribute('aria-pressed', String(this.textSelection[button.dataset.textStyle] === button.dataset.styleValue));
      }
      this.renderPricing();
      const textColor = this.textColors.get(this.textSelection.color);
      this.querySelector('[data-text-color-label]').textContent = textColor.label;
      for (const button of this.querySelectorAll('[data-text-color]')) {
        button.setAttribute('aria-pressed', String(button.dataset.textColor === textColor.id));
      }
      this.renderPhotoState();
      this.scheduleCalloutLayout();
    }

    shareUrl() {
      const url = new URL(location.href);
      for (const part of this.config.parts) url.searchParams.set(`mcase-${part.id}`, this.selection[part.id]);
      url.searchParams.set('mcase-phone-color', this.phoneColor);
      url.searchParams.set('mcase-view', this.view);
      url.searchParams.set('mcase-chin-style', this.chinStyle);
      for (const key of ['mcase-customization', 'mcase-custom-text', 'mcase-text', 'mcase-text-color', 'mcase-text-font', 'mcase-text-size', 'mcase-text-align', 'mcase-text-outline']) url.searchParams.delete(key);
      if (this.textSelection.enabled) {
        url.searchParams.set('mcase-custom-text', '1');
        if (this.textSelection.mode === 'blank') url.searchParams.set('mcase-customization', 'blank');
        else {
          url.searchParams.set('mcase-text', this.textSelection.text);
          url.searchParams.set('mcase-text-color', this.textSelection.color);
          for (const key of ['font','size','align','outline']) url.searchParams.set(`mcase-text-${key}`, this.textSelection[key]);
        }
      }
      return url.href;
    }

    async shareMCase() {
      const url = this.shareUrl();
      const fallback = this.querySelector('[data-mcase-share-link]');
      fallback.value = url;
      fallback.hidden = false;
      fallback.focus();
      fallback.select();
      this.status('Copy the selected MCase link.');
      try {
        if (navigator.share) {
          await navigator.share({ title: 'My MCase for Titan 2 Elite', url });
          fallback.hidden = true;
          this.status('MCase shared.');
          return;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
      try {
        await navigator.clipboard.writeText(url);
        fallback.hidden = true;
        this.status('MCase link copied.');
      } catch {
        fallback.value = url;
        fallback.hidden = false;
        fallback.focus();
        fallback.select();
        this.status('Copy the selected MCase link.');
      }
    }

    randomizeColors() {
      const palette = [...this.colors.keys()];
      if (!palette.length) return false;
      this.closeColorMenu(false);
      for (const part of this.config.parts) {
        this.selection[part.id] = palette[Math.floor(Math.random() * palette.length)];
      }
      this.render();
      this.status('Random colorway applied. You can still customize any module.');
      return true;
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || button.disabled) return;
      if (button.dataset.previewMode) {
        if (!['design', 'photo'].includes(button.dataset.previewMode)) return;
        if (button.dataset.previewMode === 'photo' && !this.matchingPhoto()) return;
        this.closeColorMenu(false);
        this.previewMode = button.dataset.previewMode;
        this.render();
        this.saveDraft();
      } else if (button.dataset.phoneColor) {
        if (!['black', 'orange'].includes(button.dataset.phoneColor)) return;
        this.closeColorMenu(false);
        this.phoneColor = button.dataset.phoneColor;
        this.render();
        this.presetImageUrls.forEach(url => URL.revokeObjectURL(url));
        this.presetImageUrls = [];
        this.renderPresetPreviews();
        history.replaceState(null, '', this.shareUrl());
        this.saveDraft();
        this.status(`${this.phoneColor === 'orange' ? 'Orange' : 'Black'} phone preview selected.`);
      } else if (button.dataset.chin) {
        this.closeColorMenu(false);
        this.chinStyle = button.dataset.chin;
        this.render();
        this.presetImageUrls.forEach(url => URL.revokeObjectURL(url));
        this.presetImageUrls = [];
        this.renderPresetPreviews();
        history.replaceState(null, '', this.shareUrl());
        this.saveDraft();
        this.status(this.chinStyle === 'long' ? 'Extended Chin selected · Approx. 3 mm longer.' : 'Chin selected.');
      } else if (button.hasAttribute('data-mcase-share')) {
        this.shareMCase();
      } else if (button.dataset.textColor) {
        if (!this.textSelection.enabled || !this.textColors.has(button.dataset.textColor)) return;
        this.closeColorMenu(false);
        this.textSelection.color = button.dataset.textColor;
        this.clearTextUrl();
        this.view = 'back';
        this.render();
        this.status(`Text color: ${this.textColors.get(this.textSelection.color).label}`);
      } else if (button.hasAttribute('data-text-reset')) {
        this.textSelection.text = '';
        this.querySelector('[data-backplate-text-input]').value = this.textSelection.text;
        this.clearTextUrl();
        this.closeColorMenu(false);
        this.view = 'back';
        this.render();
        this.status('Text cleared. Enter your text or turn off customization.');
      } else if (button.dataset.preset) {
        const preset = this.presets.find(preset => preset.id === button.dataset.preset);
        if (!preset) return;
        this.closeColorMenu(false);
        this.selection = { ...preset.colors };
        this.render();
        this.status(`${preset.label} applied. You can still customize any module.`);
      } else if (button.dataset.pickerOpen) {
        if (this.activePicker === button.dataset.pickerOpen && this.hoverOpenedPicker === this.activePicker) {
          this.hoverOpenedPicker = null;
          this.querySelector(`[data-module="${this.activePicker}"].sm-mcase__color-menu [aria-pressed="true"]`).focus({ preventScroll: true });
        } else {
          this.openColorMenu(button.dataset.pickerOpen);
        }
      } else if (button.dataset.color) {
        this.closeColorMenu(true);
        this.selection[button.dataset.module] = button.dataset.color;
        this.render();
        this.status(`${this.config.parts.find(part => part.id === button.dataset.module).label}: ${this.colors.get(button.dataset.color).label}`);
      } else if (button.dataset.view) {
        this.closeColorMenu(false);
        this.view = button.dataset.view;
        this.render();
      } else if (button.hasAttribute('data-reset')) {
        this.closeColorMenu(false);
        for (const part of this.config.parts) this.selection[part.id] = part.default;
        const url = new URL(location.href);
        for (const part of this.config.parts) url.searchParams.delete(`mcase-${part.id}`);
        url.searchParams.delete('mcase-view');
        history.replaceState(null, '', url);
        this.render();
        this.status('Default colors restored.');
      } else if (button.hasAttribute('data-preview-open')) {
        const clone = this.previewMode === 'photo'
          ? this.querySelector('[data-photo-window]').cloneNode(true)
          : this.exportSvg();
        if (this.previewMode === 'photo') {
          clone.removeAttribute('data-photo-window');
          for (const property of ['width', 'height', 'left', 'top']) clone.style.removeProperty(property);
          clone.querySelector('img').removeAttribute('data-photo-image');
        }
        this.querySelector('[data-dialog-svg]').replaceChildren(clone);
        this.querySelector('[data-preview-dialog]').showModal();
      } else if (button.hasAttribute('data-preview-close')) {
        this.querySelector('[data-preview-dialog]').close();

      }
    }

    async backPreviewPng(selection = this.selection, textSelection = this.textSelection) {
      if (!this.svgs.back) throw new Error('The back preview is still loading.');
      const frozenText = { ...textSelection };
      // Clone before the first await: later edits must not change this cart item.
      const svg = this.exportSvg('back', { ...selection }, frozenText);
      const font = this.textFonts.get(frozenText.font) || this.textFonts.get('outfit');
      let fontTimeout;
      try {
        await Promise.race([
          document.fonts.load(`${font.weight} 100px ${font.family}`),
          new Promise((_, reject) => { fontTimeout = setTimeout(() => reject(new Error('The preview font could not load.')), 8000); })
        ]);
      } finally {
        clearTimeout(fontTimeout);
      }
      this.applyBackplateText(svg, frozenText);
      const bounds = svg.viewBox.baseVal;
      const width = 780;
      const height = Math.ceil(width * bounds.height / bounds.width);
      svg.setAttribute('width', String(width));
      svg.setAttribute('height', String(height));
      const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml;charset=utf-8' }));
      try {
        const image = new Image();
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('The back preview could not render.')), 8000);
          image.onload = () => { clearTimeout(timeout); resolve(); };
          image.onerror = () => { clearTimeout(timeout); reject(new Error('The back preview could not render.')); };
          image.src = url;
        });
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const context = canvas.getContext('2d');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
        context.drawImage(image, 0, 0, width, height);
        return await new Promise((resolve, reject) => canvas.toBlob(blob =>
          blob ? resolve(blob) : reject(new Error('The back preview could not be saved.')), 'image/png'));
      } finally {
        URL.revokeObjectURL(url);
      }
    }

    exportSvg(view = this.view, selection = this.selection, textSelection = this.textSelection) {
      const current = this.svgs[view];
      const clone = current.cloneNode(true);
      // Resolve live CSS colors so enlarged previews match the selected modules.
      for (const part of this.config.parts) {
        const color = this.colors.get(selection[part.id]);
        for (const variable of part.variables) {
          for (const node of clone.querySelectorAll(`[data-part="${variable}"]`)) node.style.fill = color.hex;
        }
      }
      this.renderPhoneColor(clone);
      this.updateLockscreenClock(clone);
      this.applyBackplateText(clone, textSelection);
      this.applyModelLogos(clone, selection);
      return clone;
    }

  }
  customElements.define('mcase-color-customizer', MCaseCustomizer);
})();
